import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { GuestAnswer } from './GuestAnswer';
import { LEXI_ENABLED } from '../../lib/features';
import './lexi-clean.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mentor/guest`;
const id = () => window.crypto.randomUUID();
const ended = s => ['completed', 'failed', 'unavailable', 'cancelled', 'reconciliation_pending'].includes(s);
const explain = e => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'Lexi could not connect. Try Send again to recover this question.'; };

export default function LexiChat({ open, onClose, prefill = '' }) {
  const [state, setState] = useState(null), [input, setInput] = useState(prefill), [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const conversation = useRef(id()), turn = useRef(null), active = useRef(false), busyRef = useRef(false);
  const timer = useRef(null), transcript = useRef(null);
  const load = async () => {
    const { data } = await axios.get(`${API}/session`, { withCredentials: true });
    if (active.current) { setState(data); if (!data.generation_enabled) setError(data.message || 'Lexi is temporarily unavailable.'); }
    return data;
  };
  const poll = async requestId => {
    try {
      const { data } = await axios.get(`${API}/questions/${requestId}`, { withCredentials: true });
      if (!active.current) return;
      if (ended(data.status)) { await load(); setBusy(false); busyRef.current = false; turn.current = null; }
      else timer.current = setTimeout(() => poll(requestId), 1000);
    } catch (e) { if (active.current) { setError(explain(e)); setBusy(false); busyRef.current = false; } }
  };
  useEffect(() => {
    if (!open || !LEXI_ENABLED) return;
    active.current = true;
    load().then(async data => {
      if (data.generation_enabled && data.status === 'cookie_required') {
        await axios.post(`${API}/start`, {}, { withCredentials: true }); data = await load();
      }
      if (!active.current) return;
      const last = data.questions?.at(-1);
      if (last) conversation.current = last.session_id;
      const pending = data.questions?.find(q => ['reserved', 'dispatching'].includes(q.status));
      if (pending) { busyRef.current = true; setBusy(true); poll(pending.request_id); }
    }).catch(e => { if (active.current) setError(explain(e)); });
    return () => { active.current = false; clearTimeout(timer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { transcript.current?.scrollTo({ top: transcript.current.scrollHeight, behavior: 'auto' }); }, [state?.questions?.length, busy, error]);
  const send = async e => {
    e.preventDefault();
    if (!LEXI_ENABLED || busyRef.current || !input.trim() || state?.remaining === 0) return;
    if (new TextEncoder().encode(input).length > 4000) { setError('Please shorten your question.'); return; }
    // A lost response reuses its exact request ID and payload, never a paid retry.
    if (turn.current && turn.current.message !== input.trim()) { setError('Use Send to recover your previous question before entering another.'); setInput(turn.current.message); return; }
    turn.current ||= { request_id: id(), session_id: conversation.current, message: input.trim() };
    busyRef.current = true; setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/questions`, turn.current, { withCredentials: true });
      if (!active.current) return;
      setInput(''); await load();
      if (ended(data.status)) { setBusy(false); busyRef.current = false; turn.current = null; }
      else poll(data.request_id);
    } catch (e) {
      if (!active.current) return;
      setError(explain(e)); setBusy(false); busyRef.current = false;
      const definitive = [400, 401, 403, 422, 428, 429].includes(e.response?.status)
        || e.response?.data?.detail?.code === 'spending_limits_missing';
      if (definitive) turn.current = null;
      else if (turn.current) setInput(turn.current.message);
    }
  };
  if (!open || !LEXI_ENABLED) return null;
  const questions = (state?.questions || []).filter(q => !q.answer?.mocked);
  return <section className="lexi-clean" aria-label="Lexi chat" data-testid="lexi-chat-window">
    <header className="lexi-clean-header"><h2 data-testid="lexi-header-name">Lexi</h2><span className="lexi-clean-count" data-testid="lexi-remaining" aria-label={`${state?.remaining ?? 3} answers remaining`}>{state?.remaining ?? 3} left</span><button type="button" onClick={onClose} title="Close Lexi" aria-label="Close Lexi" data-testid="lexi-close-btn"><X size={22} /></button></header>
    <div className="lexi-clean-conversation" ref={transcript} role="log" aria-live="polite" data-testid="lexi-messages">
      {questions.map((q,i) => <div className="lexi-clean-turn" key={q.request_id}><p className="lexi-user-question" data-testid={`lexi-user-message-${i}`}>{q.message}</p>{q.answer && <GuestAnswer answer={q.answer} index={i} />}</div>)}
      {busy && <div className="lexi-clean-loading" role="status" data-testid="lexi-request-status"><span /><span /><span /><span className="sr-only">Lexi is answering</span></div>}
      {state?.remaining === 0 ? <p className="lexi-clean-notice" role="status" data-testid="lexi-limit-message">You’ve used your 3 free answers. You can continue exploring all LearnixTa pathways.</p> : error && <p className="lexi-clean-error" role="alert" data-testid="lexi-error">{error}</p>}
    </div>
    <form className="lexi-clean-composer" onSubmit={send}><label className="sr-only" htmlFor="lexi-question">Ask a question</label><textarea id="lexi-question" data-testid="lexi-input" placeholder="Ask a question…" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(e); } }} rows={2} maxLength={4000} disabled={busy || state?.remaining === 0} /><button type="submit" data-testid="lexi-send-btn" disabled={busy || !input.trim() || state?.remaining === 0}>Send</button></form>
  </section>;
}